import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStoreStatus } from "@/context/StoreStatusContext";

function formatCountdown(ms) {
  if (ms <= 0) return "";
  const totalMinutes = Math.floor(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return "under a minute";
}

/**
 * Site-wide closed banner. Renders the operator/schedule message and — when
 * we know when the next shift starts — a live "Reopens in Xh Ym" countdown
 * that ticks every 30s. Silent when the store is open.
 */
export default function StoreClosedBanner() {
  const { storeStatus, refresh } = useStoreStatus();
  const [remaining, setRemaining] = useState("");
  const bannerRef = useRef(null);

  useEffect(() => {
    const nextIso = storeStatus?.next_open_at;
    if (!nextIso) { setRemaining(""); return; }
    const target = new Date(nextIso).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setRemaining("");
        // Reopen just happened — refresh the whole store status so the
        // banner disappears without a manual reload.
        refresh?.();
        return;
      }
      setRemaining(formatCountdown(diff));
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [storeStatus?.next_open_at, refresh]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const banner = bannerRef.current;
    if (!banner || storeStatus?.is_open) {
      root.style.setProperty("--store-closed-banner-height", "0px");
      return undefined;
    }

    const updateHeight = () => {
      root.style.setProperty("--store-closed-banner-height", `${banner.offsetHeight}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(banner);
    return () => {
      observer.disconnect();
      root.style.setProperty("--store-closed-banner-height", "0px");
    };
  }, [storeStatus?.is_open, storeStatus?.closed_message, remaining]);

  if (!storeStatus || storeStatus.is_open) return null;

  return (
    <div
      ref={bannerRef}
      data-testid="store-closed-banner"
      className={
        "fixed inset-x-0 top-0 z-50 bg-espresso px-4 py-2.5 "
        + "font-serif text-sm italic tracking-wide text-ivory"
      }
      role="status"
    >
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1 text-center">
        <span>{storeStatus.closed_message}</span>
        {remaining && (
          <span
            data-testid="store-closed-countdown"
            className="not-italic text-ivory/80 label-xs tracking-[0.16em]"
          >
            · Reopens in {remaining}
          </span>
        )}
      </div>
    </div>
  );
}
